import type {Content} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import type {SliceComponentProps} from '@prismicio/react'
import Image from 'next/image'
import {useLinkResolver} from 'prismicio'
import type {FC} from 'react'
import {useEffect, useState} from 'react'
// eslint-disable-next-line boundaries/dependencies -- grandfathered cross-slice import, predates the slice-boundaries rule
import {ParallaxWrapper} from 'slices/AnimatedTextBlock/components/ParallaxWrapper'
import {Container} from 'src/common/Container'
import {CTAButtonWithArrow} from 'src/common/CTAButtonWithArrow'
import {getButtonsData} from 'src/common/helpers'
import {sanitizeHtml} from 'src/common/helpers/sanitizeHtml'
import type {Context} from 'src/common/types'

export type StresstimonialsFooterProps = SliceComponentProps<Content.StresstimonialsFooterSlice>

const StresstimonialsFooter: FC<StresstimonialsFooterProps & {context: Context}> = ({
  slice,
  context: {pageUid, buttonsData},
  index: componentIndex,
}) => {
  const {linkResolver} = useLinkResolver()
  const sanitizedHeading = sanitizeHtml(slice.primary.title ?? '')
  const [isMobile, setIsMobile] = useState(false)

  const {primaryButtonData} = getButtonsData({
    buttonsData,
    primaryButton: slice.primary.cta_button,
  })

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile, {passive: true})

    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  return (
    <Container
      backgroundColor="Transparent"
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="pointer-events-none mt-0 !pb-20 !pt-0 md:mt-[-350px]"
    >
      <div className="mx-auto flex w-full max-w-[1402px] flex-col items-center justify-center px-0 md:px-10 xl:px-0">
        <div className="relative w-full pt-[1370px] md:pt-[1070px]">
          {isFilled.image(slice.primary.image) && (
            <div className="absolute left-1/2 top-0 z-0 w-full max-w-[215px] -translate-x-1/2">
              <PrismicNextImage field={slice.primary.image} className="h-auto w-full" />
            </div>
          )}

          <ParallaxWrapper speed={isMobile ? 5 : 12} fadeIn={false} fadeOut={false}>
            <h1
              className="relative z-10 w-full text-center text-[87px] font-bold leading-[0.85] tracking-[-1.28px] text-[#131212] sm:text-[210px] md:tracking-[-1.92px] lg:text-[322px] lg:tracking-[-2.56px]"
              dangerouslySetInnerHTML={{__html: sanitizedHeading}}
            />
          </ParallaxWrapper>
        </div>

        {/* CTA Button - appears when hero button hides */}
        {primaryButtonData && (
          <div className="pointer-events-auto mb-12 flex justify-center md:mb-20">
            <CTAButtonWithArrow
              label={primaryButtonData.label || ''}
              href={linkResolver(primaryButtonData.link, 'stresstimonials-cta-button')}
              trackingData={{
                mktgId: primaryButtonData.marketing_id || '',
                location: `component-${componentIndex}-stresstimonials-footer`,
                dataType: primaryButtonData.intent || 'stresstimonials-cta-button',
                pageName: pageUid,
                label: primaryButtonData.label || '',
              }}
              id={`component-${componentIndex}-stresstimonials-footer-cta`}
            />
          </div>
        )}

        <div className="mt-0 w-full max-w-[800px] max-md:px-4 md:mt-8 lg:mt-[-30px]">
          <div className="flex items-center justify-center gap-3 md:gap-8">
            {isFilled.keyText(slice.primary.left_side_text) && (
              <div className="flex-1 border-y border-dashed border-moss-black py-1 md:py-2">
                <p className="whitespace-nowrap text-10 font-semibold uppercase tracking-[0.6px] text-moss-black">
                  {slice.primary.left_side_text}
                </p>
              </div>
            )}

            <Image
              src="/gif/Header_motion.gif"
              alt="Stresstimonials"
              width={141}
              height={70}
              className="h-[50px] w-auto md:h-[70px]"
              unoptimized
            />

            {isFilled.keyText(slice.primary.right_side_text) && (
              <div className="flex-1 border-y border-dashed border-moss-black py-1 md:py-2">
                <p className="whitespace-nowrap text-end text-10 font-semibold uppercase tracking-[0.6px] text-moss-black">
                  {slice.primary.right_side_text}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Container>
  )
}

export default StresstimonialsFooter
